import React, { ReactNode } from 'react';
import { useHistory } from 'react-router';
import clsx from 'clsx';
import { Box, Container, Grid, makeStyles, Theme } from '@material-ui/core';
import BookmarkIcon from '@material-ui/icons/BookmarkBorder';
import { StyledText } from '../../components/StyledText';
import { useUser } from '../../contexts';
import Button from '../../components/Button';


const useStyles = makeStyles((theme: Theme) => ({
  root: {
    background: theme.palette.background.default,
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    minWidth: theme.breakpoints.values.md,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2)
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3)
    }
  },
  body: {
    border: '1px solid',
    borderColor: theme.palette.border.main,
    borderRadius: 8,
    background: theme.palette.background.paper,
    padding: theme.spacing(7),
    '& h2': {
      marginBottom: '14px'
    },
    '& span': {
      display: 'inline-block',
      marginBottom: '32px'
    },
    '& ul': {
      paddingLeft: '20px'
    },
    '& li::marker': {
      color: theme.palette.accent.main
    },
    '& li': {
      marginBottom: '14px'
    },
    '& a': {
      color: theme.palette.secondary.main
    },
    paddingBottom: '10px'
  },
  tipWithImageRoot: {
    display: 'flex',
    marginBottom: '47px'
  },
  leftAlignedTip: {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      '& > * + *': {
        margin: '30px 0 0'
      }
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      '& > * + *': {
        margin: '0 0 0 30px'
      }
    }
  },
  rightAlignedTip: {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      '& > * + *': {
        margin: '30px 0 0'
      }
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row-reverse',
      '& > * + *': {
        margin: '0 30px 0 0'
      }
    }
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '13px',
    color: theme.palette.accent.main
  },
  tutorialTitle: {
    padding: theme.spacing(3, 0)
  },
  tipTitle: {
    marginLeft: '10px'
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: '20px'
  },
  companyButton: {
    marginTop: '20px'
  }
}));

enum TipWithImageOrientation {
  Left,
  Right
}

type TipWithImageProps = {
  orientation: TipWithImageOrientation
  title: string
  content: ReactNode
  imageUrl: string
};

const TipWithImage = ({ orientation, title, content, imageUrl }: TipWithImageProps) => {
  const styles = useStyles();
  const orientationStyle = orientation === TipWithImageOrientation.Left ? styles.leftAlignedTip : styles.rightAlignedTip;

  return (
    <Box className={clsx(styles.tipWithImageRoot, orientationStyle)}>
      <Box display='flex' flexDirection='column' justifyContent='center' width='100%'>
        <Box className={styles.titleContainer}>
          <BookmarkIcon />
          <StyledText className={styles.tipTitle} variant='h4' component='h4'>{title}</StyledText>
        </Box>
        <StyledText variant='body1' component='p'>{content}</StyledText>
      </Box>
      <Box>
        <img src={imageUrl} alt='Tip' className={styles.image} />
      </Box>
    </Box>
  );
};

export const EntrepreneurTutorial = () => {
  const styles = useStyles();
  const history = useHistory();
  const { userInfo } = useUser();
  const profileId = userInfo?.profileId;

  return (
    <Container maxWidth='xl' className={styles.root}>
      <Grid container direction='row' justify='center' alignItems='center'>
        <Grid item xs style={{ maxWidth: 960 }}>
          <StyledText variant='h1' className={styles.tutorialTitle}>Entrepreneur Tutorial</StyledText>
          <Box className={styles.body}>
            <StyledText variant='h2' component='h2'>The Platform</StyledText>
            <StyledText variant='body3' component='span'>
              CapitalRaise.net was built to help entrepreneurs increase their odds of raising venture capital.
              Within the platform, you’ll be guided to set up a profile -  the way investors like to see them.
              <br />
              <br />
              Our database will show you which investors are the strongest fit, based upon the funds’ investment criteria.
              From there, you’ll be shown trusted connectors that know investors at the identified funds.
            </StyledText>

            <TipWithImage
              orientation={TipWithImageOrientation.Right}
              title='Fun fact'
              content='Less than one percent of the companies that attempt to raise venture capital (VC) receive it.'
              imageUrl='/tutorial1.jpeg'
            />

            <StyledText variant='h2' component='h2'>Trusted Connector</StyledText>
            <StyledText variant='body3' component='span'>
              VC investors don’t like unsolicited deal flows or cold outreach. Virtually 100% of them are rejected.
              They like being referred via their network - a trusted connector.
              <br />
              <br />
              As you’re shown who these trusted connectors are, you will have the opportunity to reach out to them and cultivate a relationship with them.
              <br />
              <br />
              Every successful startup will have to hire at least one law firm, accounting firm and bank.
              This platform will show you how these firms could be strategic to your business’ success.
              Do not think of these as vendor relationships, rather think of them as strategic partners.
            </StyledText>
            <StyledText variant='h2' component='h2'>Referrals</StyledText>
            <StyledText variant='body3' component='span'>
              The trusted connectors’ relationships with VCs are their bread and butter.
              <br />
              <br />
              These relationships are carefully guarded. That is why VCs accept deal referrals from them.
              <br />
              <br />
              Connectors will need to have confidence in your company’s potential for success to refer you.
              When you reach out to the connector, be cognizant of what they’re likely to expect out of the
              relationship. For example, if a law firm introduces you to a fund, the fund will expect the
              company to use that law firm when they invest.
              <br />
              <br />
              That being said, choose your connectors carefully. If they agree to make a referral, it’ll be a double opt in.
              They will reach out to the investor you want referred to and get permission before they connect you.
              <br />
              <br />
              While the hit rate is much higher using this approach, expect that oftentimes VCs will still decline a connection for a myriad of reasons,
              such as they are too busy closing another deal, have invested in a perceived competitor, etc.
            </StyledText>

            <TipWithImage
              orientation={TipWithImageOrientation.Left}
              title='Tip'
              content='Choose your connectors carefully. Connectors will need to have confidence in your company’s potential for success to refer you.'
              imageUrl='/tutorial2.jpeg'
            />

            <StyledText variant='h2' component='h2'>Profile</StyledText>
            <StyledText variant='body3' component='span'>
              The referral source determines whether or not they look at your profile.
              If your profile is presented by a trusted referral source the investor will scan your profile and decide if they want to look further.
              <br />
              <br />
              The profile section mimics a one-page executive summary template.
              If you already have an executive summary, filling out this section will involve cutting and pasting from it.
              <br />
              <br />
              When completing this section, the rule of thumb is less is more. Just summarize your business at a high level.
              Think of this as a teaser, not a business plan. If your team has done a successful startup together in the past, state that.
              If any of your key team members has had an exit and delivered returns to investors previously, be sure to include this information.
              <br />
              <br />
              After glancing at the summary information, investors will typically look at your five-year pro forma financial projections,
              and this is the information many entrepreneurs neglect. While it’s hard to accurately project, you need to do this.
            </StyledText>

            <TipWithImage
              orientation={TipWithImageOrientation.Right}
              title='Tip'
              // eslint-disable-next-line @typescript-eslint/quotes
              content={`When you do your financial projections, it's important that you build them out with the assumption that you will raise multiple rounds of capital. With Series Seed, A, B and, perhaps, C rounds assumed, model out the growth you would generate by hiring sales and marketing professionals to grow the business. For a venture backed B2B SaaS company, the most attractive models will typically show a doubling of revenue year over year.`}
              imageUrl='/tutorial3.jpeg'
            />

            <StyledText variant='body3' component='span'>
              Don’t include your valuation. This is something that the investor will typically propose and that you will negotiate later.
            </StyledText>
            <StyledText variant='h2' component='h2'>LinkedIn</StyledText>
            <StyledText variant='body3' component='span'>
              Investors and potential connections will likely click on your profile to see if you are someone they would consider backing,
              so make sure your profile is professional and up to date with all of your accomplishments.
            </StyledText>
            <StyledText variant='h2' component='h2'>Executive Summary</StyledText>
            <StyledText variant='body3' component='span'>
              The executive summary should mirror your profile information.
              It is here to download if the investor wants to print it out to read while traveling or to share it at a partners meeting.
            </StyledText>
            <StyledText variant='h2' component='h2'>Pitch Deck</StyledText>
            <StyledText variant='body3' component='span'>
              Some investors like to flip through a pitch deck without listening to the narration.
              <br />
              <br />
              You may upload a PowerPoint deck here or link to a DocSend or Pitchly deck. Here are some
              basic slides we recommend you include:
              <ul>
                <li>Problem</li>
                <li>Solution</li>
                <li>Market Potential</li>
                <li>Management Team</li>
                <li>Go to Market Strategy</li>
                <li>Competition</li>
                <li>Financial Projections</li>
                <li>Funds Requested</li>
                <li>Use of Proceeds</li>
              </ul>
            </StyledText>

            <TipWithImage
              orientation={TipWithImageOrientation.Left}
              title='Insight'
              content={(
                <span>
                  This
                  {' '}
                  <a
                    href='https://techcrunch.com/2015/06/08/lessons-from-a-study-of-perfect-pitch-decks-vcs-spend-an-average-of-3-minutes-44-seconds-on-them/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    TechCrunch article
                  </a>
                  {' '}
                  from 2015 does a great job showing how investors look through a deck. Notice that they go to the financial slide first and they spend the most time on it.
                </span>
              )}
              imageUrl='/tutorial4.jpeg'
            />

            <StyledText variant='h2' component='h2'>Elevator Pitch</StyledText>
            <StyledText variant='body3' component='span'>
              The elevator pitch is designed to be a quick pitch that you would give an investor if you had to do it during an elevator ride.
              It should be no more than 30 seconds with no slides.
            </StyledText>
            <StyledText variant='h2' component='h2'>Full-Length Pitch</StyledText>
            <StyledText variant='body3' component='span'>
              This will be a YouTube or Vimeo recording of you going through your slide deck and pitching it.
              You may use just voice narration, or you may be on camera. We recommend limiting this pitch to eight minutes.
            </StyledText>
          </Box>
          <StyledText variant='textButton' color='secondary' component='div' className={styles.companyButton}>
            <Button variant='text' onClick={() => history.push(`/entrepreneur/${profileId}`)} color='inherit'>
              Go to My Company
            </Button>
          </StyledText>
        </Grid>
      </Grid>
    </Container>
  );
};
